import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * Wiki - Seção Gerentes de Afiliados
 *
 * Documentação completa sobre o sistema de gerentes,
 * como funciona, convites de afiliados, requisitos e gerenciamento.
 * Voltada para produtores e gerentes (não técnica).
 */
const GerentesAfiliadosWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:account-tie" />
          <span>Gerentes de Afiliados</span>
        </div>
        <p className={styles.heroText}>
          Escale suas vendas delegando o recrutamento de afiliados.
          Gerentes constroem e gerenciam suas próprias equipes de vendas.
        </p>
      </section>

      {/* O que é um Gerente */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que é um Gerente de Afiliados?</span>
          <p className={styles.sectionSubtitle}>
            Entenda o papel do gerente na sua estrutura de vendas
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:account-tie" className={styles.infoIcon} />
          <div>
            <span className={styles.infoTitle}>Definição</span>
            <p>
              O <strong>Gerente de Afiliados</strong> é um intermediário entre você (produtor) e os afiliados.
              Ele tem permissão para <strong>recrutar, convidar e gerenciar</strong> sua própria equipe de afiliados
              para promover seus produtos.
            </p>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={4}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconPrimary}`}>
                <Icon icon="mdi:crown" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Produtor</span>
                <p>
                  Você define as comissões do gerente e tem controle total sobre o produto.
                  Pode ver todos os afiliados, inclusive os do gerente.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:account-tie" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Gerente</span>
                <p>
                  Recruta afiliados, define comissões (dentro do limite) e acompanha vendas da sua equipe.
                  Pode vender diretamente também.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:account-network" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Afiliado</span>
                <p>
                  Promove o produto usando seu link exclusivo.
                  Recebe comissão definida pelo gerente.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Funciona o Sistema */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona o Sistema de Comissões</span>
          <p className={styles.sectionSubtitle}>
            Entenda a distribuição de valores entre produtor, gerente e afiliado
          </p>
        </div>

        <div className={styles.tipCard}>
          <Icon icon="mdi:lightbulb-on" className={styles.tipIcon} />
          <div>
            <span className={styles.tipTitle}>Regra de Ouro</span>
            <p>
              O gerente define a comissão do afiliado, mas ela <strong>sai do pool de comissão do gerente</strong>.
              Exemplo: se o gerente tem 40% e dá 20% ao afiliado, o gerente fica com os outros 20%.
            </p>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={6}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <Icon icon="mdi:account-tie" size={32} />
                <span className={styles.comparisonTitle}>Gerente Vende Diretamente</span>
              </div>
              <ul className={styles.featureList}>
                <li><Icon icon="mdi:check-circle" /> Gerente usa seu próprio link de checkout</li>
                <li><Icon icon="mdi:cash" /> Recebe a comissão "sem afiliado" (ex: 40%)</li>
                <li><Icon icon="mdi:account-off" /> Nenhum afiliado recebe nessa venda</li>
              </ul>
              <div className={styles.cardBadge} style={{ marginTop: '10px' }}>
                Venda: R$ 100 → Gerente: R$ 38,00 (40% de R$ 95)
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <Icon icon="mdi:account-network" size={32} />
                <span className={styles.comparisonTitle}>Afiliado do Gerente Vende</span>
              </div>
              <ul className={styles.featureList}>
                <li><Icon icon="mdi:check-circle" /> Afiliado usa seu link de checkout</li>
                <li><Icon icon="mdi:cash" /> Afiliado recebe sua comissão (ex: 20%)</li>
                <li><Icon icon="mdi:cash-multiple" /> Gerente recebe a diferença (40% - 20% = 20%)</li>
              </ul>
              <div className={styles.cardBadge} style={{ marginTop: '10px' }}>
                Venda: R$ 100 → Afiliado: R$ 19 | Gerente: R$ 19
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Criar um Gerente */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Criar um Gerente de Afiliados</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para adicionar um gerente ao seu produto
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Acesse o Produto</span>
                <p>
                  Vá em <strong>Meus Produtos</strong>, encontre o produto desejado e clique
                  em <strong>"Editar"</strong> ou <strong>"Gerenciar"</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Aba Gerentes</span>
                <p>
                  Na página do produto, acesse a aba <strong>"Gerentes de Afiliados"</strong>.
                  Você verá a lista de gerentes atuais (se houver).
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Adicionar Gerente</span>
                <p>
                  Clique em <strong>"Adicionar Gerente"</strong>. Preencha o email do usuário
                  que será gerente (ele já deve ter conta na plataforma).
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>4</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Defina as Comissões</span>
                <p>
                  Configure duas comissões:
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> <strong>Com afiliado:</strong> quando afiliado do gerente vende</li>
                  <li><Icon icon="mdi:check-circle" /> <strong>Sem afiliado:</strong> quando gerente vende diretamente</li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>

        <div className={styles.infoCard} style={{ marginTop: '20px' }}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <span className={styles.infoTitle}>Importante</span>
            <p className="mb-0">
              Quando você adiciona um gerente, ele automaticamente recebe um <strong>código de afiliado</strong>
              próprio para poder vender diretamente. Esse código aparece nos checkouts disponíveis na página
              "Produtos que Gerencio".
            </p>
          </div>
        </div>
      </section>

      {/* Convites de Afiliados */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Sistema de Convites de Afiliados</span>
          <p className={styles.sectionSubtitle}>
            Como gerentes convidam e recrutam afiliados
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Gerente Envia Convite</span>
                <p>
                  Na página <strong>"Produtos que Gerencio"</strong>, o gerente clica em
                  <strong> "Convidar Afiliado"</strong>. Informa email, nome e comissão.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Email de Convite</span>
                <p>
                  O convidado recebe um email com detalhes do produto, comissão oferecida
                  e um link para aceitar ou recusar.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Banner no Dashboard</span>
                <p>
                  Se o convidado já tem conta, ele verá um <strong>banner roxo</strong> no topo
                  do dashboard avisando sobre convites pendentes.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>4</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Aceitar ou Recusar</span>
                <p>
                  O convidado pode ver os detalhes do convite, verificar se atende os requisitos
                  e então <strong>aceitar</strong> ou <strong>recusar</strong> o convite.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Requisitos para Afiliado */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Requisitos para ser Afiliado Validado</span>
          <p className={styles.sectionSubtitle}>
            O que é necessário para aceitar convites e começar a vender
          </p>
        </div>

        <div className={styles.tipCard}>
          <Icon icon="mdi:shield-check" className={styles.tipIcon} />
          <div>
            <span className={styles.tipTitle}>Por que existem requisitos?</span>
            <p className="mb-0">
              Para garantir que as comissões possam ser pagas corretamente, todo afiliado precisa
              ter seus dados cadastrais completos e uma conta bancária aprovada.
            </p>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={6}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:account-check" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Dados Pessoais Completos</span>
                <p>
                  O usuário precisa ter preenchido todos os dados pessoais obrigatórios:
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Nome completo</li>
                  <li><Icon icon="mdi:check-circle" /> CPF ou CNPJ</li>
                  <li><Icon icon="mdi:check-circle" /> Data de nascimento</li>
                  <li><Icon icon="mdi:check-circle" /> Telefone</li>
                  <li><Icon icon="mdi:check-circle" /> Endereço completo</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:bank" />
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardTitle}>Conta Bancária Aprovada</span>
                <p>
                  É necessário ter pelo menos uma conta bancária cadastrada e aprovada:
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Conta corrente ou poupança</li>
                  <li><Icon icon="mdi:check-circle" /> Dados bancários corretos</li>
                  <li><Icon icon="mdi:check-circle" /> Status: Aprovada</li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>

        <div className={styles.infoCard} style={{ marginTop: '20px' }}>
          <Icon icon="mdi:alert-circle" className={styles.infoIcon} />
          <div>
            <span className={styles.infoTitle}>Se os requisitos não forem atendidos</span>
            <p className="mb-0">
              O convite ainda pode ser visualizado, mas o botão <strong>"Aceitar"</strong> ficará desabilitado.
              O sistema mostra exatamente o que está faltando para que o usuário complete seu cadastro.
            </p>
          </div>
        </div>
      </section>

      {/* Página Produtos que Gerencio */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Página "Produtos que Gerencio"</span>
          <p className={styles.sectionSubtitle}>
            Onde o gerente administra seus produtos e afiliados
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <Icon icon="mdi:view-dashboard" size={32} />
                <span className={styles.comparisonTitle}>Visão Geral</span>
              </div>
              <p>
                Lista todos os produtos onde o usuário é gerente, mostrando:
              </p>
              <ul className={styles.featureList}>
                <li><Icon icon="mdi:package" /> Nome e imagem do produto</li>
                <li><Icon icon="mdi:cash-multiple" /> Suas comissões configuradas</li>
                <li><Icon icon="mdi:account-group" /> Quantidade de afiliados recrutados</li>
                <li><Icon icon="mdi:chart-line" /> Total de vendas da equipe</li>
              </ul>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <Icon icon="mdi:cog" size={32} />
                <span className={styles.comparisonTitle}>Ações Disponíveis</span>
              </div>
              <p>
                O gerente pode realizar várias ações em cada produto:
              </p>
              <ul className={styles.featureList}>
                <li><Icon icon="mdi:account-plus" /> Convidar novos afiliados</li>
                <li><Icon icon="mdi:account-edit" /> Editar afiliados existentes</li>
                <li><Icon icon="mdi:link" /> Copiar links de checkout</li>
                <li><Icon icon="mdi:eye" /> Ver detalhes de vendas</li>
              </ul>
            </div>
          </Col>
        </Row>

        <Row className="g-4 mt-2">
          <Col lg={6}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:link-variant" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Checkouts Disponíveis</span>
                <p>
                  O gerente tem acesso aos links de checkout <strong>com seu código de afiliado</strong>.
                  Pode copiar e compartilhar para vender diretamente.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:account-supervisor" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Gerenciar Afiliados</span>
                <p>
                  Visualiza todos os afiliados que recrutou, podendo editar comissões,
                  ver status e acompanhar performance individual.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Dúvidas comuns sobre gerentes de afiliados
          </p>
        </div>

        <div className={styles.faqContainer}>
          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>O gerente pode definir qualquer comissão para seus afiliados?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                <strong>Não.</strong> A comissão do afiliado não pode exceder a comissão "com afiliado"
                do gerente. Por exemplo, se o gerente tem 40% de comissão com afiliado, ele pode
                dar no máximo 40% para um afiliado (ficando com 0% para si).
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>O produtor pode ver os afiliados do gerente?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                <strong>Sim!</strong> O produtor tem visibilidade total. Em "Meus Afiliados",
                os afiliados recrutados por gerentes aparecem com uma indicação do gerente
                responsável. O produtor pode inclusive editar a comissão desses afiliados.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>O que acontece se um gerente for removido?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                Os afiliados recrutados por ele continuam ativos no produto, mas passam a ser
                considerados "afiliados diretos" do produtor. O produtor pode então decidir
                manter, editar ou remover esses afiliados.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>Posso ter múltiplos gerentes no mesmo produto?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                <strong>Sim!</strong> Você pode adicionar quantos gerentes quiser a um produto.
                Cada um terá sua própria equipe de afiliados e suas próprias comissões configuradas.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>Como o gerente acompanha suas vendas?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                O gerente pode ver suas vendas (diretas e da equipe) na página
                <strong> "Produtos que Gerencio"</strong> e também no <strong>Financeiro</strong>,
                onde aparecem as comissões recebidas.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>Convites pendentes expiram?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                Não, convites não expiram automaticamente. O convidado pode aceitar ou recusar
                a qualquer momento. O gerente pode cancelar convites pendentes se necessário.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GerentesAfiliadosWiki;
